import React, { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { toggleFollow } from '@/lib/follow';
import { cn } from '@/lib/utils';

interface FollowButtonProps {
  userId: string;
  isFollowing: boolean;
  onFollowChange: (isFollowing: boolean) => void;
  className?: string;
}

export function FollowButton({ userId, isFollowing, onFollowChange, className }: FollowButtonProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleFollow = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const newFollowState = await toggleFollow(userId);
      onFollowChange(newFollowState);
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleFollow}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={loading || user?.id === userId}
      className={cn(
        "px-6 py-2 rounded-lg font-medium transition-all duration-200",
        isFollowing
          ? "bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-600"
          : "bg-royal-purple text-white hover:bg-royal-purple/90",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        className
      )}
    >
      {loading ? (
        <div className="flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        </div>
      ) : isFollowing ? (
        isHovered ? 'Unfollow' : 'Following'
      ) : (
        'Follow'
      )}
    </button>
  );
}